"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { useLenis } from "lenis/react";

// How much clearance to leave under the fixed nav when a section is taller than
// the viewport and can't be centered.
const NAV_CLEARANCE = 24;

// Computes the scroll position that centers an element in the viewport. Sections
// shorter than the viewport sit dead-centre; taller ones land just under the nav
// so their heading never scrolls out of view.
const centeredTarget = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  const elTop = rect.top + window.scrollY;
  const gap = (window.innerHeight - rect.height) / 2;
  const topGap = gap > NAV_CLEARANCE ? gap : NAV_CLEARANCE;
  return Math.max(0, elTop - topGap);
};

// Intercepts in-page #anchor clicks (and the initial hash on load) and scrolls
// so the target section is centered, instead of pinned below the nav.
export const useAnchorScroll = () => {
  const $ = _c(3);
  const lenis = useLenis();
  let t0;
  let t1;
  if ($[0] !== lenis) {
    t0 = () => {
      const scrollToHash = (hash, smooth) => {
        const id = hash.slice(1);
        if (!id) {
          return;
        }
        const el = document.getElementById(id);
        if (!el) {
          return;
        }
        const top = centeredTarget(el);
        if (lenis) {
          lenis.scrollTo(top, {
            duration: smooth ? 1.2 : 0
          });
        } else {
          window.scrollTo({
            top,
            behavior: smooth ? "smooth" : "auto"
          });
        }
      };
      const onClick = e => {
        const link = (e.target as HTMLElement)?.closest?.("a");
        const href = link?.getAttribute("href");
        if (!href || !href.startsWith("#")) {
          return;
        }
        e.preventDefault();
        history.replaceState(null, "", href);
        scrollToHash(href, true);
      };
      document.addEventListener("click", onClick);
      if (window.location.hash) {
        requestAnimationFrame(() => scrollToHash(window.location.hash, false));
      }
      return () => document.removeEventListener("click", onClick);
    };
    t1 = [lenis];
    $[0] = lenis;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
};